import { useEffect, useLayoutEffect, useRef, useState, type SyntheticEvent } from 'react';
import { CheckIcon, MailIcon, MinusIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import renderKitCopy from '@/i18n/ns/render-kit';
import { useOptionalWorkspace } from '@/workspace/context';
import { useChatInterfaceLocale } from '@/lib/chat-i18n';
import { actionSurfaceCopy } from '@/components/assistant-ui/action-surface-copy';
import { useActionExpiry } from '@/components/assistant-ui/action-expiry';
import { useActionConfirmation } from '@/components/assistant-ui/use-action-confirmation';
import { routeFor } from '@/workspace/navigation';
import { writeWorkspaceHash } from '@/lib/chat-links';
import type {
  ActionOrigin,
  ActionPointer,
  ActionView,
} from '@/components/assistant-ui/action-confirmation';
import { useDraftSend } from './draft-send';
import { RecipientField } from './recipient-field';
import { EmailBody } from './email-message';
import { addressTokens, draftFields, validAddress, type EmailDraftFields } from './email-model';

type Props = { pointer: ActionPointer; fresh: boolean; origin: ActionOrigin };
type Pending = { to: string; cc: string; bcc: string };
const NO_PENDING: Pending = { to: '', cc: '', bcc: '' };
const BODY_MAX_PX = 560;

/**
 * render_email_draft: the draft is the staged send_email action itself.
 * Editing changes only this card; Send restages the edited fields (a new
 * approval digest), presents that exact version and approves it, all inside
 * the person's one trusted click. The assistant can never press it.
 */
export function EmailDraftCard(props: Props) {
  const workspace = useOptionalWorkspace();
  const surface = actionSurfaceCopy(useChatInterfaceLocale());
  // A reader of a shared chat cannot fetch the owner's private draft.
  if (workspace?.readOnly)
    return (
      <section className="pc-action-status-line" role="status">
        {surface.waitingFor(workspace.threadMeta?.ownerName ?? '')}
      </section>
    );
  return <OwnedEmailDraft {...props} />;
}

function outcome(action: ActionView, expired: boolean) {
  if (action.status === 'pending') return expired ? 'expired' : 'pending';
  if (action.status === 'executed') return 'sent';
  if (action.status === 'running') return 'sending';
  if (action.status === 'rejected' || action.status === 'superseded') return 'discarded';
  if (action.status === 'expired') return 'expired';
  const receipt = action.result as { action?: { state?: string } } | undefined;
  // A failed dispatch with no provider receipt, or an unknown provider
  // state, may still have sent: never claim it did not.
  return receipt?.action?.state === 'failed' ? 'failed' : 'unknown';
}

function OwnedEmailDraft({ pointer, fresh, origin }: Props) {
  const state = useActionConfirmation(pointer, 'compact', fresh, origin);
  const { action, loadError } = state;
  const copy = useNamespaceCopy(renderKitCopy);
  const [fields, setFields] = useState<EmailDraftFields | null>(null);
  const [pending, setPending] = useState<Pending>(NO_PENDING);
  const [showCopies, setShowCopies] = useState(false);
  const { busy, notice, writeNeeded, send: sendDraft } = useDraftSend(state);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const expiry = useActionExpiry(action?.expiresAt ?? '');
  // The text grows with the email, wrapped lines included, then scrolls.
  useLayoutEffect(() => {
    const field = bodyRef.current;
    if (!field) return;
    field.style.height = 'auto';
    field.style.height = `${Math.min(field.scrollHeight + 2, BODY_MAX_PX)}px`;
  }, [fields?.body]);
  useEffect(() => {
    if (!action) return;
    const loaded = draftFields(action.args);
    setFields(loaded);
    setPending(NO_PENDING);
    setShowCopies(loaded.cc.length > 0 || loaded.bcc.length > 0);
  }, [action?.id]);
  if (!action || !fields)
    return (
      <section className="pc-action-status-line" role="status">
        {loadError ? copy.unavailable : copy.loading}
      </section>
    );
  const shown = outcome(action, expiry.expired);
  // While a send is in flight the fields stay, locked, and Send says Sending….
  const editable = shown === 'pending' && action.canDecide;
  const connection = action.decision.connection;
  const sender = [connection?.name, connection?.account].filter(Boolean).join(' · ');
  // A Gmail reply keeps the conversation's subject: shown, not editable.
  const fixedSubject = connection?.replyKeepsSubject
    ? formatCopy(copy.replyKeepsSubject, { name: connection.name })
    : null;
  const setRecipients = (key: keyof Pending) => (values: string[], text: string) => {
    setFields((current) => current && { ...current, [key]: values });
    setPending((current) => ({ ...current, [key]: text }));
  };
  // Typed but uncommitted addresses count as entered; an invalid one stops Send.
  const finalFields = (): EmailDraftFields | null => {
    const merged = { ...fields };
    for (const key of ['to', 'cc', 'bcc'] as const) {
      const tokens = addressTokens(pending[key]);
      if (tokens.some((token) => !validAddress(token))) return null;
      merged[key] = [...new Set([...fields[key], ...tokens])];
    }
    return merged;
  };
  const send = (event: SyntheticEvent<HTMLButtonElement>) => {
    if (event.nativeEvent.isTrusted !== true || !editable || busy || !state.canApprove) return;
    const next = finalFields();
    if (!next) return;
    if (!next.to.length || !next.body.trim()) {
      if (!next.body.trim()) bodyRef.current?.focus();
      return;
    }
    void sendDraft(action, next);
  };
  const discard = () => void state.mutate('reject');
  // Writing is allowed only in Connections, never from this card: open that
  // connection's access, and the draft waits here for the next Send.
  const openAccess = () =>
    writeWorkspaceHash(
      `${routeFor('connections')}?access=${encodeURIComponent(String(action.args.connectionId ?? ''))}`,
    );
  return (
    <div
      ref={state.presentation.ref}
      role="group"
      className="pc-email-card pc-email-draft"
      data-renderer="email_draft"
      data-state={shown}
      data-action-id={action.id}
      aria-label={copy.draftCard}
      aria-busy={busy || shown === 'sending' ? true : undefined}
    >
      <header className="pc-email-head">
        <MailIcon aria-hidden="true" />
        <h3 className="pc-email-subject">{copy.draftCard}</h3>
        <DraftStatus shown={shown} />
      </header>
      {sender && (
        <div className="pc-draft-row pc-draft-from">
          <span className="pc-draft-label">{copy.from}</span>
          <span>{sender}</span>
        </div>
      )}
      {editable ? (
        <>
          <RecipientField
            label={copy.to}
            values={fields.to}
            pending={pending.to}
            disabled={busy}
            onChange={setRecipients('to')}
          />
          {showCopies ? (
            <>
              <RecipientField
                label={copy.cc}
                values={fields.cc}
                pending={pending.cc}
                disabled={busy}
                onChange={setRecipients('cc')}
              />
              <RecipientField
                label={copy.bcc}
                values={fields.bcc}
                pending={pending.bcc}
                disabled={busy}
                onChange={setRecipients('bcc')}
              />
            </>
          ) : (
            <button type="button" className="pc-draft-copies" onClick={() => setShowCopies(true)}>
              {copy.showCcBcc}
            </button>
          )}
          <div className="pc-draft-row">
            {fixedSubject ? (
              <>
                <span className="pc-draft-label">{copy.subject}</span>
                <span className="pc-draft-fixed">{fixedSubject}</span>
              </>
            ) : (
              <>
                <label htmlFor={`${action.id}-subject`}>{copy.subject}</label>
                <input
                  id={`${action.id}-subject`}
                  className="pc-draft-subject"
                  value={fields.subject}
                  readOnly={busy}
                  maxLength={998}
                  onChange={(event) => setFields({ ...fields, subject: event.target.value })}
                  onInput={(event) => setFields({ ...fields, subject: event.currentTarget.value })}
                />
              </>
            )}
          </div>
          <textarea
            ref={bodyRef}
            className="pc-draft-body"
            aria-label={copy.message}
            value={fields.body}
            readOnly={busy}
            maxLength={50_000}
            rows={6}
            onChange={(event) => setFields({ ...fields, body: event.target.value })}
            onInput={(event) => setFields({ ...fields, body: event.currentTarget.value })}
          />
          {writeNeeded && (
            <div className="pc-draft-access" role="status">
              <p>
                {formatCopy(
                  connection?.canChangeAccess === false
                    ? copy.writeAccessAsk
                    : copy.writeAccessNeeded,
                  { name: connection?.name ?? '' },
                )}
              </p>
              <Button size="sm" variant="outline" onClick={openAccess}>
                {copy.openConnections}
              </Button>
            </div>
          )}
          {notice && (
            <p className="pc-draft-error" role="alert">
              {notice}
            </p>
          )}
          <footer className="pc-draft-actions">
            <Button variant="ghost" size="sm" disabled={busy} onClick={discard}>
              {copy.discard}
            </Button>
            <Button
              size="sm"
              data-pending-focus=""
              // Focusable before its card grant arrives and while it sends, so
              // focus stays here; send() ignores a click until it may send.
              aria-disabled={busy || !state.canApprove || undefined}
              className="aria-disabled:opacity-50"
              onClick={send}
            >
              {busy ? copy.sending : copy.send}
            </Button>
          </footer>
        </>
      ) : (
        <SentDraft
          fields={fields}
          shown={shown}
          service={connection?.name ?? ''}
          fixedSubject={fixedSubject}
        />
      )}
    </div>
  );
}

function DraftStatus({ shown }: { shown: ReturnType<typeof outcome> }) {
  const copy = useNamespaceCopy(renderKitCopy);
  // Pending shows the controls; an unknown outcome is explained below the draft.
  if (shown === 'pending' || shown === 'unknown') return null;
  const label = shown === 'sent' ? copy.sent : shown === 'sending' ? copy.sending : copy.discarded;
  const Icon = shown === 'sent' ? CheckIcon : shown === 'sending' ? MailIcon : MinusIcon;
  return (
    <span className="pc-email-status" data-state={shown} role="status">
      <Icon aria-hidden="true" />
      {label}
    </span>
  );
}

function SentDraft({
  fields,
  shown,
  service,
  fixedSubject,
}: {
  fields: EmailDraftFields;
  shown: ReturnType<typeof outcome>;
  service: string;
  fixedSubject: string | null;
}) {
  const copy = useNamespaceCopy(renderKitCopy);
  const problem =
    shown === 'expired'
      ? copy.expired
      : shown === 'failed'
        ? copy.failed
        : shown === 'unknown'
          ? formatCopy(copy.unknown, { name: service })
          : null;
  return (
    <>
      <dl className="pc-email-recipients pc-draft-sent">
        <div>
          <dt>{copy.to}</dt>
          <dd>{fields.to.join(', ')}</dd>
        </div>
        {fields.cc.length > 0 && (
          <div>
            <dt>{copy.cc}</dt>
            <dd>{fields.cc.join(', ')}</dd>
          </div>
        )}
        {fields.bcc.length > 0 && (
          <div>
            <dt>{copy.bcc}</dt>
            <dd>{fields.bcc.join(', ')}</dd>
          </div>
        )}
        <div>
          <dt>{copy.subject}</dt>
          <dd>{fixedSubject ?? (fields.subject || copy.noSubject)}</dd>
        </div>
      </dl>
      <EmailBody body={fields.body} />
      {problem && (
        <p className="pc-draft-error" role="status">
          {problem}
        </p>
      )}
    </>
  );
}
